import React, { useState } from 'react';
import { Button } from 'src/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'src/components/ui/Card';
import { Badge } from 'src/components/ui/Badge';
import { Separator } from 'src/components/ui/Separator';
import { 
  ExternalLink, 
  Copy, 
  CheckCircle, 
  AlertTriangle,
  Info,
  Bug
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';

interface TeamsJoinDebuggerProps {
  sessionId: string;
  meetingMetadata?: {
    provider: string;
    join_url: string;
    join_url_browser?: string;
    join_url_direct?: string;
    join_url_fallback?: string;
    meeting_id: string;
    debug_info?: {
      tenant_id?: string;
      creation_time?: string;
      api_response_keys?: string[];
    };
  };
  className?: string;
}

export const TeamsJoinDebugger: React.FC<TeamsJoinDebuggerProps> = ({
  sessionId,
  meetingMetadata,
  className = '',
}) => {
  const { toast } = useToast();
  const [testResults, setTestResults] = useState<Record<string, 'pending' | 'success' | 'error'>>({});

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied",
        description: `${label} copied to clipboard`,
      });
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const testUrl = async (url: string, urlType: string) => {
    setTestResults(prev => ({ ...prev, [urlType]: 'pending' }));
    
    try {
      // Open URL in new window for testing
      const testWindow = window.open(url, '_blank', 'width=800,height=600');
      
      // Check if window opened successfully
      if (testWindow) {
        setTestResults(prev => ({ ...prev, [urlType]: 'success' }));
        toast({
          title: "Test Window Opened",
          description: `${urlType} URL opened for testing`,
        });
      } else {
        setTestResults(prev => ({ ...prev, [urlType]: 'error' }));
        toast({
          title: "Popup Blocked",
          description: "Please allow popups and try again",
          variant: "destructive",
        });
      }
    } catch (error) {
      setTestResults(prev => ({ ...prev, [urlType]: 'error' }));
      toast({
        title: "Test Failed",
        description: `Failed to test ${urlType} URL`,
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (status: 'pending' | 'success' | 'error' | undefined) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'pending':
        return <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />;
      default:
        return <Info className="h-4 w-4 text-gray-400" />;
    }
  };

  const urlOptions = [
    {
      key: 'direct',
      label: 'Direct Web URL',
      url: meetingMetadata?.join_url_direct,
      description: 'Constructed Teams web URL with anonymous context',
      priority: 1
    },
    {
      key: 'browser',
      label: 'Browser Optimized URL',
      url: meetingMetadata?.join_url_browser,
      description: 'Original URL with browser-friendly parameters',
      priority: 2
    },
    {
      key: 'fallback',
      label: 'Fallback URL',
      url: meetingMetadata?.join_url_fallback,
      description: 'Minimal parameter URL',
      priority: 3
    },
    {
      key: 'original',
      label: 'Original URL',
      url: meetingMetadata?.join_url,
      description: 'Unmodified URL from Microsoft Graph API',
      priority: 4
    }
  ].filter(option => option.url);

  if (!meetingMetadata) {
    return (
      <Card className={className}>
        <CardContent className="p-6 text-center text-gray-500">
          No meeting metadata available for debugging
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bug className="h-5 w-5 text-orange-600" />
          Teams Join Debugger
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Meeting Information */}
        <div className="space-y-2">
          <h4 className="font-medium">Meeting Information</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>Session ID:</div>
            <div className="font-mono text-xs">{sessionId}</div>
            <div>Meeting ID:</div>
            <div className="font-mono text-xs">{meetingMetadata.meeting_id}</div>
            <div>Provider:</div>
            <div>{meetingMetadata.provider}</div>
            {meetingMetadata.debug_info?.tenant_id && (
              <>
                <div>Tenant ID:</div>
                <div className="font-mono text-xs">{meetingMetadata.debug_info.tenant_id}</div>
              </>
            )}
            {meetingMetadata.debug_info?.creation_time && (
              <>
                <div>Created:</div>
                <div className="text-xs">{new Date(meetingMetadata.debug_info.creation_time).toLocaleString()}</div>
              </>
            )}
          </div>
        </div>

        <Separator />

        {/* URL Testing */}
        <div className="space-y-4">
          <h4 className="font-medium">URL Testing (Priority Order)</h4>
          
          {urlOptions.map((option) => (
            <div key={option.key} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">#{option.priority}</Badge>
                  <span className="font-medium">{option.label}</span>
                  {getStatusIcon(testResults[option.key])}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(option.url!, option.label)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => testUrl(option.url!, option.key)}
                    disabled={testResults[option.key] === 'pending'}
                  >
                    <ExternalLink className="h-3 w-3" />
                    Test
                  </Button>
                </div>
              </div>
              
              <p className="text-xs text-gray-600">{option.description}</p>
              
              <div className="bg-gray-50 p-2 rounded text-xs font-mono break-all">
                {option.url}
              </div>
            </div>
          ))}
        </div>

        <Separator />

        {/* Troubleshooting Tips */}
        <div className="space-y-2">
          <h4 className="font-medium">Troubleshooting Tips</h4>
          <div className="text-sm space-y-1 text-gray-600">
            <p>• Test URLs in incognito/private browser window</p>
            <p>• Check if "Join as guest" option appears</p>
            <p>• Verify Azure AD tenant allows anonymous meetings</p>
            <p>• If all URLs redirect to login, check tenant configuration</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const bestUrl = urlOptions[0]?.url;
              if (bestUrl) {
                window.open(bestUrl, '_blank', 'noopener,noreferrer');
              }
            }}
            className="flex-1"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Try Best URL
          </Button>
          
          <Button
            variant="outline"
            onClick={() => {
              const debugInfo = {
                sessionId,
                meetingMetadata,
                urlOptions,
                testResults,
                timestamp: new Date().toISOString()
              };
              copyToClipboard(JSON.stringify(debugInfo, null, 2), 'Debug Info');
            }}
            className="flex-1"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Debug Info
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TeamsJoinDebugger;